import React from 'react';
import { motion } from 'framer-motion';
import { 
  AccountTree, 
  Cloud, 
  Code, 
  Storage, 
  Security, 
  CheckCircle, 
  Settings,
  GitHub,
  AccountTree as GitMerge
} from '@mui/icons-material';
import ScrollHeader from '../components/ScrollHeader';
import MotionDiv from '../components/MotionDiv';

const Integrations = () => {
  const sourceControlPlatforms = [
    {
      name: 'GitHub',
      description: 'A web-based platform for version control, issue tracking, and project management.',
      icon: '/assets/icons/scanners/github.png',
      features: ['Repository scanning', 'Pull request integration', 'Issue tracking', 'Automated workflows']
    },
    {
      name: 'Bitbucket',
      description: 'A web-based platform for version control and collaboration, focusing on code review and CI/CD.',
      icon: '/assets/icons/scanners/bitbucket.png',
      features: ['Repository scanning', 'Pipeline integration', 'Code review', 'Branch policies']
    },
    {
      name: 'GitLab',
      description: 'A complete DevOps platform for software development and deployment.',
      icon: '/assets/icons/scanners/gitlab.png',
      features: ['Repository scanning', 'CI/CD integration', 'Security scanning', 'Issue management']
    }
  ];

  const thirdPartyIntegrations = [
    {
      name: 'Invicti',
      description: 'Advanced web application security testing and vulnerability scanning platform.',
      icon: '/assets/icons/scanners/invicti.png',
      category: 'DAST Scanner'
    },
    {
      name: 'Checkmarx',
      description: 'Advanced web application security testing and vulnerability scanning platform.',
      icon: '/assets/icons/scanners/checkmarx.png',
      category: 'SAST Scanner'
    },
    {
      name: 'Wiz',
      description: 'Cloud infrastructure security platform for continuous risk discovery and mitigation.',
      icon: '/assets/icons/scanners/wiz.png',
      category: 'Cloud Security'
    }
  ];

  const sastScanners = [
    {
      name: 'Snyk',
      description: 'Find and automatically fix vulnerabilities in your code, open source dependencies, containers, and infrastructure as code.',
      icon: '/assets/icons/scanners/snyk.svg'
    },
    {
      name: 'Checkmarx',
      description: 'Enterprise-grade static application security testing for custom code security.',
      icon: '/assets/icons/scanners/checkmarx.png'
    },
    {
      name: 'Fortify',
      description: 'Identify and fix security vulnerabilities in your application code early in the development lifecycle.',
      icon: '/assets/icons/scanners/fortify.webp'
    },
    {
      name: 'SonarQube',
      description: 'Clean code platform for continuous code quality and security analysis.',
      icon: '/assets/icons/scanners/sonarqube.webp'
    }
  ];

  const dastScanners = [
    {
      name: 'VeraCode',
      description: 'Enterprise-grade dynamic application security testing platform.',
      icon: '/assets/icons/scanners/veracode.png'
    },
    {
      name: 'Checkmarx One',
      description: 'Advanced DAST solution for comprehensive web application security testing.',
      icon: '/assets/icons/scanners/checkmarx.png'
    },
    {
      name: 'Acunetix',
      description: 'Automated web application security testing with advanced vulnerability scanning.',
      icon: '/assets/icons/scanners/acunetix.png'
    },
    {
      name: 'AppCheck Web Application Scanner',
      description: 'Comprehensive web application and infrastructure security testing platform.',
      icon: '/assets/icons/scanners/appcheck.png'
    },
    {
      name: 'Invicti',
      description: 'Advanced web application security scanner with proof-based scanning.',
      icon: '/assets/icons/scanners/invicti.png'
    },
    {
      name: 'Qualys',
      description: 'Cloud-based web application scanning and security testing.',
      icon: '/assets/icons/scanners/qualys.png'
    },
    {
      name: 'HCL AppScan',
      description: 'Enterprise application security testing and management platform.',
      icon: '/assets/icons/scanners/hcl.png'
    },
    {
      name: 'AppSpider',
      description: 'Dynamic application security testing for modern web applications.',
      icon: '/assets/icons/scanners/appspider.png'
    }
  ];

  const infrastructureScanners = [
    {
      name: 'Anchore Engine Scan',
      description: 'Comprehensive container image security scanning and policy evaluation.',
      icon: '/assets/icons/scanners/anchore.png'
    },
    {
      name: 'Aqua Scan',
      description: 'Advanced container security scanning and vulnerability detection platform.',
      icon: '/assets/icons/scanners/aqua.png'
    },
    {
      name: 'AWS Security Hub',
      description: 'Centralized security findings and compliance checks for AWS resources.',
      icon: '/assets/icons/scanners/aws.png'
    },
    {
      name: 'Qualys Infrastructure Scan',
      description: 'Comprehensive infrastructure vulnerability assessment and security testing.',
      icon: '/assets/icons/scanners/qualys.png'
    },
    {
      name: 'Tenable Scan',
      description: 'Enterprise vulnerability management and security assessment.',
      icon: '/assets/icons/scanners/tenable.png'
    },
    {
      name: 'Wiz Scan',
      description: 'Comprehensive cloud infrastructure security scanning and analysis.',
      icon: '/assets/icons/scanners/wiz.png'
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Integrations" />
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
            Ana Security integrates with your favorite development tools, security scanners, and cloud platforms to provide comprehensive security coverage across your entire development stack.
          </p>
          
          <div className="mb-8">
            <img 
              src="/screenshots/integrations/12_integrations.png" 
              alt="Integrations Dashboard"
              className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full max-w-4xl mx-auto"
            />
          </div>
        </div>

        {/* Source Control Platforms */}
        <MotionDiv delay={0.1}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <AccountTree className="w-6 h-6 text-primary-500" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Source Control Platforms
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sourceControlPlatforms.map((platform, index) => {
                const getIcon = () => {
                  switch (platform.name) {
                    case 'GitHub':
                      return <GitHub className="w-8 h-8 text-gray-800" />;
                    case 'Bitbucket':
                      return <GitMerge className="w-8 h-8 text-blue-600" />;
                    case 'GitLab':
                      return <GitMerge className="w-8 h-8 text-orange-600" />;
                    default:
                      return <AccountTree className="w-8 h-8 text-gray-600" />;
                  }
                };

                return (
                  <div key={index} className="bg-light-hover dark:bg-dark-hover border border-light-border dark:border-dark-border rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white rounded-lg p-2 flex items-center justify-center">
                        <img 
                          src={platform.icon} 
                          alt={platform.name}
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {platform.name}
                      </h3>
                    </div>
                  
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {platform.description}
                    </p>
                    
                    <div className="space-y-2">
                      {platform.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <div className="w-1 h-1 bg-primary-500 rounded-full"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </MotionDiv>

        {/* Third Party Integrations */}
        <MotionDiv delay={0.2}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Cloud className="w-6 h-6 text-primary-500" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Third Party Integrations
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {thirdPartyIntegrations.map((integration, index) => (
                <div key={index} className="bg-light-hover dark:bg-dark-hover border border-light-border dark:border-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white rounded-lg p-2 flex items-center justify-center">
                      <img 
                        src={integration.icon} 
                        alt={integration.name}
                        className="w-8 h-8 object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {integration.name}
                      </h3>
                      <p className="text-xs text-primary-500">
                        {integration.category}
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {integration.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* SAST Scanners */}
        <MotionDiv delay={0.3}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Code className="w-6 h-6 text-primary-500" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                SAST Scanners
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Static Application Security Testing tools for analyzing source code vulnerabilities.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {sastScanners.map((scanner, index) => (
                <div key={index} className="bg-light-hover dark:bg-dark-hover border border-light-border dark:border-dark-border rounded-lg p-4 text-center">
                  <div className="w-16 h-16 bg-white rounded-lg p-2 flex items-center justify-center mx-auto mb-3">
                    <img 
                      src={scanner.icon} 
                      alt={scanner.name}
                      className="w-12 h-12 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {scanner.name}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {scanner.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* DAST Scanners */}
        <MotionDiv delay={0.4}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Security className="w-6 h-6 text-primary-500" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                DAST Scanners
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Dynamic Application Security Testing tools for runtime vulnerability assessment.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {dastScanners.map((scanner, index) => (
                <div key={index} className="bg-light-hover dark:bg-dark-hover border border-light-border dark:border-dark-border rounded-lg p-4 text-center">
                  <div className="w-16 h-16 bg-white rounded-lg p-2 flex items-center justify-center mx-auto mb-3">
                    <img 
                      src={scanner.icon} 
                      alt={scanner.name}
                      className="w-12 h-12 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {scanner.name}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {scanner.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Infrastructure Scanners */}
        <MotionDiv delay={0.5}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Storage className="w-6 h-6 text-primary-500" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Infrastructure Scanners
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Cloud and infrastructure security scanning tools for comprehensive environment assessment.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {infrastructureScanners.map((scanner, index) => (
                <div key={index} className="bg-light-hover dark:bg-dark-hover border border-light-border dark:border-dark-border rounded-lg p-4 text-center">
                  <div className="w-16 h-16 bg-white rounded-lg p-2 flex items-center justify-center mx-auto mb-3">
                    <img 
                      src={scanner.icon} 
                      alt={scanner.name}
                      className="w-12 h-12 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {scanner.name}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {scanner.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Integration Benefits */}
        <MotionDiv delay={0.6}>
          <div className="text-center bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Why Integrate with Ana Security?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Streamline your security workflow with seamless integrations across your development stack.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Automated Workflows</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Trigger security scans automatically on code commits, pull requests, and deployments.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Security className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Unified Security</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Centralize security findings from multiple tools in one comprehensive dashboard.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Easy Configuration</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Simple setup process with guided configuration for all supported integrations.
                </p>
              </div>
            </div>
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default Integrations;
